import { BehaviorSubject, filter, fromEvent, Observable, Subject, take } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Data, Params, Router } from '@angular/router';

import { SessionFacade } from '@frontend-site/shared/core';
import { removeTrailingSlash } from '@frontend-site/shared/utils';
import { AnalysisData, SessionKeys } from '@frontend-site/shared/data';
import {
    HYBRID_CONFIG,
    HybridConfig,
    HybridMessage,
    HybridMessagePayload,
    HybridMessageSubject,
    HybridMessageSubSubject
} from '../entities';
import { HybridMessagePayloadService } from './hybrid-message-payload.service';
import { HybridIframeManagerService } from '../services/hybrid-iframe-manager.service';
import { RouterStoreService } from '@frontend-site/shared/router-store';

@Injectable({
    providedIn: 'root'
})
export class HybridCommunicationService {
    public sessionId?: string;
    private messageStack: { subject: HybridMessageSubject; payload?: HybridMessagePayload }[] = [];
    private uuidToSubject: any = {};

    private _iframe?: HTMLIFrameElement;
    set iframe(value: HTMLIFrameElement) {
        this._iframe = value;
    }

    private _isIframeCollapsed = false;

    set isIframeCollapsed(value: boolean) {
        this._isIframeCollapsed = value;

        if (this.shouldIgnoreOldUiSignals$.value !== this.isNoIframePresent) {
            this.shouldIgnoreOldUiSignals$.next(this.isNoIframePresent);
        }
    }

    private _isFloatingIframe: boolean = false;

    set isFloatingIframe(value: boolean) {
        this._isFloatingIframe = value;

        if (this.shouldIgnoreOldUiSignals$.value !== this.isNoIframePresent) {
            this.shouldIgnoreOldUiSignals$.next(this.isNoIframePresent);
        }
    }

    private debugMode = false; //todo: add toggle for this
    private canDeactivateOldRouteSubject = new Subject<boolean>();
    private canDeactivate$ = this.canDeactivateOldRouteSubject.asObservable();

    private get oldUiCurrentlyHidden(): boolean {
        return this.iframeManager.getProperty('renderType') === 'hidden';
    }

    public readonly shouldIgnoreOldUiSignals$ = new BehaviorSubject<boolean>(this.isNoIframePresent);

    constructor(
        @Inject(DOCUMENT) private readonly doc: Document,
        @Inject(HYBRID_CONFIG) private readonly config: HybridConfig,
        private readonly router: Router,
        private readonly routerStore: RouterStoreService,
        private readonly session: SessionFacade,
        private hybridMessagePayloadService: HybridMessagePayloadService,
        private readonly iframeManager: HybridIframeManagerService
    ) {
        fromEvent<MessageEvent>(window, 'message')
            .pipe(filter((event) => typeof event.data === 'object'))
            .subscribe((event: MessageEvent) => this.processMessage(event.data));

        this.routerStore.state$.subscribe((state) => {
            this.onNavigationEnd(
                { ...state.params, ...state.queryParams },
                state.data?.navState,
                state.data?.paramConverter,
                state.data?.onContainer,
                state.data?.hybridStateCondition,
                state.data
            );
        });
        this.iframeManager.showOldApp$.subscribe((showOld) => {
            let shouldIgnore = !showOld && !this._isFloatingIframe;
            if (this.shouldIgnoreOldUiSignals$.value !== shouldIgnore) {
                this.shouldIgnoreOldUiSignals$.next(shouldIgnore);
            }
        });
    }

    sendMessage(subject: HybridMessageSubject, payload?: HybridMessagePayload, forceMsg?: boolean, callBackUid?: string): void {
        const iframe = this.iframeManager.getProperty('iframe');

        if (!iframe?.contentWindow || !this.sessionId) {
            if (subject === 'Navigation' || subject === 'OnExit') {
                this.messageStack.push({ subject, payload });
            }
            return;
        }

        if (this.isNoIframePresent && !forceMsg) {
            return;
        }

        payload = { ...payload, force: forceMsg }; // indicated if old ui need to force navigation even if its the same state
        if (iframe != null && iframe.contentWindow != null) {
            iframe.contentWindow.postMessage({ subject, sessionId: this.sessionId, payload, callBackUid }, '*');
        }
    }

    forceMessage(subject: HybridMessageSubject, payload?: HybridMessagePayload): void {
        this.sendMessage(subject, payload, true);
    }

    canDeactivateOldSite(): Observable<boolean> {
        this.sendMessage('OnBeforeUnload', undefined, true);

        return this.canDeactivate$;
    }

    sendOnCreateDefect(newDefectId: number, newDefectTitle: string, stepsToReproduce?: boolean): void {
        this.sendMessage('onCreateNewDefect', { newDefectId, newDefectTitle, stepsToReproduce }, true);
    }

    sendImportFromAnotherProjectCompleted(): void {
        this.sendMessage('onImportFromAnotherProject', {}, true);
    }

    sendCreateTestSetCompleted(): void {
        this.sendMessage('createTestSetCompleted', {}, true);
    }

    sendSwitchAccount(companyData: { id: string | number; name: string }): void {
        this.sendMessage('SwitchAccount', companyData, true);
    }

    public sendSetPlanVsActualCycleId(cycleId: number, projectId: number) {
        this.sendMessage('setPlanVsActualCycleId', { cycleId, projectId }, true);
    }

    sendSfClearAnalysis(): void {
        this.sendMessage('SfClearAnalysis');
    }

    sendClearOldSiteStateStorage(): void {
        this.sendMessage('clearAllStateStorage', {}, true);
    }

    sendUpdateNavigationIds(navigationIds: any[], total: number): void {
        this.sendMessage('updateNavigationIds', { navigationIds, total }, true);
    }

    sendprevState(prevState: string): void {
        this.sendMessage('prevState', { prevState }, true);
    }

    sendLogout(): void {
        this.forceMessage('Logout', {});
    }

    sendMessageWithCallback(subject: HybridMessageSubject, payload?: HybridMessagePayload): Observable<any> {
        const callBackUid = 'callback' + '-' + Math.floor(Math.random() * 10000);
        const currentSubject = new Subject<any>();
        const observer = currentSubject.asObservable().pipe(take(1));
        this.uuidToSubject[callBackUid] = currentSubject;
        this.sendMessage(subject, payload, true, callBackUid);
        return observer;
    }

    sendHybridScriptViewMsg(
        payload: HybridMessagePayload,
        subSubject: HybridMessageSubSubject,
        isWithCallBack = false
    ): Observable<any> | void {
        if (isWithCallBack) {
            return this.sendMessageWithCallback('hybridScriptServiceMsg', { ...payload, subSubject });
        } else {
            this.sendMessage('hybridScriptServiceMsg', { ...payload, subSubject }, true);
        }
    }

    private shouldIgnoreMessage(message: HybridMessage): boolean {
        const isLoadedSignal = message.subject === 'Loaded';
        const isForced = message.payload?.['force'];
        return this.isNoIframePresent && !isForced && !isLoadedSignal;
    }

    private processMessage(message: HybridMessage): void {
        if (this.shouldIgnoreMessage(message)) {
            return;
        }

        switch (message.subject) {
            case 'Loaded':
                this.processLoaded(message);
                break;

            case 'Navigation': {
                const url = this.cleanUrlFromOldUi(message.payload['url']);
                const replaceUrl = message.payload['isNewUiUrl'] ? false : true; // dont replace url when popup navigation
                const skipLocationChange = !!message.payload['skipLocationChange'];

                if (message.payload['navigationIds']) {
                    this.hybridMessagePayloadService.updateNavigationStore(message.payload['navigationIds'].idsList);
                }

                if (message.payload['testTabFilter']) {
                    this.hybridMessagePayloadService.sessionSet(message.payload['testTabFilter'], SessionKeys.RISK_ANALYSIS_TEST);
                }
                if (message.payload['state']) {
                    this.router.navigateByUrl(url, { replaceUrl, skipLocationChange, state: message.payload['state'] });
                } else {
                    this.router.navigateByUrl(url, { replaceUrl, skipLocationChange });
                }
                break;
            }

            case 'LoggedOut':
                this.processLogout();
                break;
            case 'ContinueNavigation': {
                const isContinue = Boolean(message.payload?.['continue']);
                this.canDeactivateOldRouteSubject.next(isContinue);
                break;
            }
            case 'Title':
                this.hybridMessagePayloadService.setTitle(message.payload['title']);
                break;
            case 'SetAnalysisData':
                this.hybridMessagePayloadService.setAnalysisData(message.payload as AnalysisData);
                break;
            case 'RefreshRequirement':
                this.hybridMessagePayloadService.refreshRequirement(message.payload['requirement']);
                break;
            case 'LoadProjectInformation':
                this.hybridMessagePayloadService.loadProjectInformation(message.payload['projectId']);
                break;
            case 'LoadSystem':
                this.hybridMessagePayloadService.loadSystems();
                break;
            case 'LoadSession':
                this.hybridMessagePayloadService.loadSession();
                break;
            case 'Busy':
                this.hybridMessagePayloadService.setBusy(message.payload['busy'] === true);
                break;
            case 'Releases':
                this.hybridMessagePayloadService.updateReleases(message.payload['releases']);
                break;
            case 'FreezeMainHeader':
                this.hybridMessagePayloadService.toggleFreezeMainHeader(Boolean(message.payload?.['freezeMainHeader']));
                break;
            case 'UpdateMyItems':
                this.hybridMessagePayloadService.updateMyItems();
                break;
            case 'OptimizeCycle':
                this.hybridMessagePayloadService.optimizeCycle();
                break;
            case 'hybridScriptServiceMsg':
                this.hybridMessagePayloadService.handleScriptViewServiceMsg(
                    message.payload,
                    message.payload['subSubject'] as HybridMessageSubSubject,
                    this.uuidToSubject,
                    message.callBackUid
                );
                break;
        }
    }

    private cleanUrlFromOldUi(url: string): string {
        const pathAndQuery = url.split('?');
        const path = removeTrailingSlash(pathAndQuery[0]);
        const queryString = pathAndQuery[1] ? `?${pathAndQuery[1]}` : '';
        return `${path}${queryString}`;
    }

    private processLoaded(message: HybridMessage): void {
        this.session.setLegacyAppVersion(message.payload?.['appVersion']);
        this.sessionId = message.sessionId;
        this.session.loadSession();

        this.processMessageStack();
        this.hybridMessagePayloadService.setBusy(false);
    }

    private processLogout(): void {
        this.session.clear();
        this.hybridMessagePayloadService.setBusy(false);
    }

    private processMessageStack(): void {
        if (this.messageStack.length === 0) {
            return;
        }

        const message = this.messageStack.pop();

        if (message) {
            this.sendMessage(message.subject, message.payload, true);
        }
    }

    private get isNoIframePresent(): boolean {
        return this.oldUiCurrentlyHidden && !this._isFloatingIframe;
    }

    public setBusyManually(value: boolean): void {
        this.hybridMessagePayloadService.setBusy(value);
    }

    private onNavigationEnd(
        params: Params,
        navState?: string,
        paramConverter?: (p: Params, currentStateData?: Data) => Params,
        fromContainer?: boolean,
        hybridStateCondition?: (d: Data | undefined) => boolean,
        currentStateData?: Data
    ): void {
        if (!navState || navState === 'login') return;

        if (hybridStateCondition && !hybridStateCondition(currentStateData)) {
            return;
        }

        if (paramConverter) {
            params = paramConverter(params, currentStateData);
        }
        const location = !!fromContainer ? false : 'replace';
        //todo: decide how to handle navigation from New to Old in Panaysh. Popup? Event that will be caught by popup service?
        this.sendMessage('Navigation', { navState, params, location }, this.oldUiCurrentlyHidden); // force message on the first route outside of new angular app
    }
}
